/**
 * /operations/attendance/ot-calculator — M3X.ATTENDANCE F.23 KEYSTONE.
 *
 * Interactive Article 107 OT calculator. HR enters OT type +
 * hours + basic salary + customer multiplier (optional) → sees
 * computed amount + statutory floor + audit trace.
 *
 * Mutaba'aa veto (422) renders inline rose banner with article
 * citation. Mode-invariant per commandment 45.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "../components/ui";
import { api } from "../lib/api";

interface OtResult {
  hours: string;
  basic_hourly: string;
  multiplier_applied: string;
  statutory_floor_multiplier: string;
  amount: string;
  currency: string;
  article_citation: string;
  audit_trace: Record<string, unknown>;
}

export function Article107OtCalculatorPage() {
  const { t } = useTranslation();
  const [floors, setFloors] = useState<Record<string, string>>({});
  const [otType, setOtType] = useState("NORMAL_OT");
  const [hours, setHours] = useState("2");
  const [basicSalary, setBasicSalary] = useState("5000");
  const [customerMultiplier, setCustomerMultiplier] = useState("");
  const [gender, setGender] = useState("male");
  const [isJuvenile, setIsJuvenile] = useState(false);
  const [isExempt, setIsExempt] = useState(false);
  const [fridayConsent, setFridayConsent] = useState(false);
  const [result, setResult] = useState<OtResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getOtFloorMultipliers().then(setFloors).catch(() => {});
  }, []);

  async function compute() {
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const r = await api.computeOt({
        ot_type: otType,
        hours,
        basic_salary: basicSalary,
        customer_multiplier: customerMultiplier || null,
        employee_gender: gender,
        employee_is_juvenile: isJuvenile,
        is_exempt_industry: isExempt,
        friday_consent_recorded: fridayConsent,
      });
      setResult(r);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t(
          "ot_calc.title",
          "Article 107 OT Premium Calculator",
        )}
        subtitle={t(
          "ot_calc.subtitle",
          "100% DET money math (commandment 3). Mutaba'aa enforces Articles 101 / 102 / 107 / 108 / 159 floors mode-invariantly.",
        )}
      />

      {/* Floor reference */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <h3 className="mb-2 text-sm font-semibold text-amber-900">
          ✦ KSA statutory floor multipliers (Mutaba'aa cannot be relaxed)
        </h3>
        <table className="w-full text-xs">
          <thead className="text-amber-800">
            <tr>
              <th className="text-start">OT type</th>
              <th className="text-end">Floor multiplier</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(floors).map(([k, v]) => (
              <tr key={k} className="border-t border-amber-200">
                <td className="py-1 font-mono">{k}</td>
                <td className="py-1 text-end font-mono">{v}x</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Inputs */}
      <div className="rounded-lg border border-slate-200 bg-surface-default p-5">
        <h3 className="mb-3 text-base font-semibold">
          {t("ot_calc.inputs_title", "Compute OT Amount")}
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-xs text-text-secondary">OT type</span>
            <select
              value={otType}
              onChange={(e) => setOtType(e.target.value)}
              className="mt-1 w-full rounded-md border border-border-default p-2"
            >
              <option value="NORMAL_OT">NORMAL_OT (1.5x)</option>
              <option value="HOLIDAY_OT">HOLIDAY_OT (2.0x)</option>
              <option value="FRIDAY_OT">FRIDAY_OT (2.0x — consent required)</option>
              <option value="NIGHT_OT">NIGHT_OT (1.75x)</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-xs text-text-secondary">Hours</span>
            <input
              type="number"
              min="0"
              max="24"
              step="0.5"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="mt-1 w-full rounded-md border border-border-default p-2"
            />
          </label>
          <label className="block text-sm">
            <span className="text-xs text-text-secondary">Basic salary (SAR)</span>
            <input
              type="number"
              min="0"
              value={basicSalary}
              onChange={(e) => setBasicSalary(e.target.value)}
              className="mt-1 w-full rounded-md border border-border-default p-2"
            />
          </label>
          <label className="block text-sm">
            <span className="text-xs text-text-secondary">
              Customer multiplier (optional; below floor → Mutaba'aa veto)
            </span>
            <input
              type="number"
              step="0.05"
              placeholder="leave blank for floor"
              value={customerMultiplier}
              onChange={(e) => setCustomerMultiplier(e.target.value)}
              className="mt-1 w-full rounded-md border border-border-default p-2"
            />
          </label>
          <label className="block text-sm">
            <span className="text-xs text-text-secondary">Employee gender</span>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="mt-1 w-full rounded-md border border-border-default p-2"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </label>
          <div className="space-y-1 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isJuvenile}
                onChange={(e) => setIsJuvenile(e.target.checked)}
              />
              <span>Employee is juvenile (&lt;18) — Art 159 night veto</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isExempt}
                onChange={(e) => setIsExempt(e.target.checked)}
              />
              <span>LE in exempt industry (Art 108)</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={fridayConsent}
                onChange={(e) => setFridayConsent(e.target.checked)}
              />
              <span>Friday consent recorded (Art 101)</span>
            </label>
          </div>
        </div>
        <button
          type="button"
          onClick={compute}
          disabled={busy}
          className="mt-3 rounded-md bg-brand-blue px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {busy
            ? t("ot_calc.computing", "Computing…")
            : t("ot_calc.compute", "Compute Article 107 OT amount")}
        </button>
      </div>

      {/* Result */}
      {result && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5">
          <h3 className="mb-3 text-base font-semibold text-emerald-900">
            ✓ {t("ot_calc.result_title", "OT Premium Computed (DET)")}
          </h3>
          <table className="w-full text-sm">
            <tbody>
              <tr>
                <td className="py-1 text-text-tertiary">Hours</td>
                <td className="py-1 text-end font-mono">{result.hours}</td>
              </tr>
              <tr>
                <td className="py-1 text-text-tertiary">Basic hourly</td>
                <td className="py-1 text-end font-mono">{result.basic_hourly}</td>
              </tr>
              <tr>
                <td className="py-1 text-text-tertiary">Multiplier applied</td>
                <td className="py-1 text-end font-mono">
                  {result.multiplier_applied}x
                </td>
              </tr>
              <tr>
                <td className="py-1 text-text-tertiary">Statutory floor</td>
                <td className="py-1 text-end font-mono text-text-tertiary">
                  {result.statutory_floor_multiplier}x
                </td>
              </tr>
              <tr className="border-t-2 border-emerald-300">
                <td className="py-2 text-base font-bold text-emerald-900">
                  Amount
                </td>
                <td className="py-2 text-end font-mono text-lg font-bold text-emerald-900">
                  {result.amount} {result.currency}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-[10px] font-mono text-text-tertiary">
            {result.article_citation}
          </p>
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-brand-blue">
              Audit trace (commandment 5)
            </summary>
            <pre className="mt-1 max-h-48 overflow-auto rounded bg-white p-2 text-[10px]">
              {JSON.stringify(result.audit_trace, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {err && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900">
          ✦ {err}
        </div>
      )}
    </div>
  );
}

export default Article107OtCalculatorPage;
